import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDownIcon, RefreshIcon, SparklesIcon } from '@utils/icons';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import type { AiBriefInsight, AiBriefInsights, AiBriefRef, AiBriefSection } from '@apis/aiAgent';
import { useBriefInsights, useRegenerateBriefInsights } from './useBriefInsights';
import { hasNotes } from '../InstructionStatus/hasNotes';
import { InstructionLines } from '../InstructionStatus/InstructionLines';
import instructionStyles from '../InstructionStatus/instructionStatus.module.css';
import styles from './briefInsights.module.css';

/**
 * THE BRIEF'S ONE MODEL-WRITTEN PASSAGE — wherever the brief is read.
 *
 * It lives on its own because the same passage is read in two places: the
 * brief's own sheet in the assistant, and the morning card on the dashboard.
 * Both must look like the assistant wrote them — the tinted panel, the
 * sparkles mark, the question you can press — because that material is how a
 * reader tells what was WRITTEN from what was READ, and a second copy of it
 * would be a second answer to that question.
 *
 * What differs between the surfaces is only where the evidence lives: on the
 * sheet each insight jumps to its own ledger line, on the dashboard there is
 * no ledger to jump to and the section is named in plain text. That is
 * `hrefOf` — absent means "name it, don't link it", which is a statement about
 * the surface, not about the insight.
 *
 * IT FETCHES ITS OWN. The insights are the reader's, written on their first
 * read of the day — a model call on the server — so they land on their own
 * schedule, seconds after the page. The panel is therefore always there and
 * says which state it is in: writing, written, nothing to lead with, or failed.
 * It never simply disappears, because a panel that vanished cannot be told
 * apart from one that was never written.
 */
interface IBriefInsightsProps {
  siteId: string;
  /** Where this surface keeps the evidence. Absent: the section is named, not linked. */
  hrefOf?: (section: AiBriefSection['key']) => string;
  /**
   * Hands the insight's question to whatever chat this surface has, with the
   * reference that lets the chat be given the same rows the insight rests on.
   */
  onAsk: (question: string, briefRef?: AiBriefRef) => void;
}

/** The same signature, down to the item that supplies the reference. */
type AskFromInsight = IBriefInsightsProps['onAsk'];

export function BriefInsights({ siteId, hrefOf, onAsk }: IBriefInsightsProps) {
  const { t } = useTranslation();
  const query = useBriefInsights(siteId);
  const regenerate = useRegenerateBriefInsights(siteId);
  const writing = query.isLoading || regenerate.isPending;

  let body: ReactNode;
  if (writing) {
    body = <p className={styles.notice}>{t('aiAgent.brief.insightsWriting')}</p>;
  } else if (query.data === undefined) {
    body = (
      <div className={styles.notice}>
        <p className={styles.noticeLine}>{t('aiAgent.brief.insightsError')}</p>
        <button type="button" className={styles.retry} onClick={() => void query.refetch()}>
          {t('aiAgent.brief.retry')}
        </button>
      </div>
    );
  } else {
    body = (
      <Written
        written={query.data}
        failedAgain={regenerate.isError}
        hrefOf={hrefOf}
        onAsk={onAsk}
      />
    );
  }

  return (
    <section className={styles.panel} aria-busy={writing}>
      {/* The mark carries the provenance: written here, read everywhere else.
          A line of prose saying so would be a line printed every single
          morning, which is one too many for something that never changes. */}
      <div className={styles.head}>
        <Tooltip content={t('aiAgent.brief.insightsSource')}>
          <span className={styles.mark}>
            <SparklesIcon size={15} />
          </span>
        </Tooltip>
        <h3 className={styles.label}>{t('aiAgent.brief.insightsLabel')}</h3>
        {/* Only once there is a reading to replace: before that, the panel is
            already writing one. */}
        {query.data !== undefined && (
          <Tooltip content={t('aiAgent.brief.regenerate')}>
            <button
              type="button"
              className={styles.regenerate}
              aria-label={t('aiAgent.brief.regenerate')}
              disabled={writing}
              onClick={() => regenerate.mutate()}
            >
              <span className={styles.regenerateIcon} data-spinning={regenerate.isPending}>
                <RefreshIcon size={14} />
              </span>
            </button>
          </Tooltip>
        )}
      </div>
      {body}
    </section>
  );
}

interface IWrittenProps {
  written: AiBriefInsights;
  /** The last attempt to write them again failed; what is shown is the earlier reading. */
  failedAgain: boolean;
  hrefOf?: (section: AiBriefSection['key']) => string;
  onAsk: AskFromInsight;
}

/**
 * A reading, in its two parts: the state of the site, then what deserves
 * attention, each item wearing its severity. Nothing needing attention is its
 * own sentence — NOT a claim that the site is quiet, which the overview and the
 * ledger make or do not make on their own.
 */
function Written({ written, failedAgain, hrefOf, onAsk }: IWrittenProps) {
  const { t } = useTranslation();
  const states = new Map(written.sectionStates.map((section) => [section.key, section.state]));

  return (
    <>
      {failedAgain && (
        <p className={styles.failedAgain} role="alert">
          {t('aiAgent.brief.regenerateError')}
        </p>
      )}
      {written.instruction !== null && <Instruction instruction={written.instruction} />}
      {written.overview !== '' && <Overview written={written} />}
      <h4 className={styles.subhead}>{t('aiAgent.brief.attentionLabel')}</h4>
      {written.insights.length === 0 ? (
        <p className={styles.quiet}>{t('aiAgent.brief.attentionEmpty')}</p>
      ) : (
        <ol className={styles.list}>
          {written.insights.map((insight, at) => (
            <li key={`${insight.section}-${at}`} className={styles.insight}>
              <p className={styles.headline}>
                <span className={styles.severity} data-severity={insight.severity}>
                  {t(`aiAgent.brief.severity.${insight.severity}`)}
                </span>
                {insight.headline}
              </p>
              <Why insight={insight} />
              <p className={styles.foot}>
                {/* The question IS the control: a reader should never have to
                    translate advice into something to type. */}
                <button
                  type="button"
                  className={styles.ask}
                  onClick={() => onAsk(insight.ask, { section: insight.section, insight: at })}
                >
                  {insight.ask}
                </button>
                <Evidence section={insight.section} state={states.get(insight.section)} hrefOf={hrefOf} />
              </p>
            </li>
          ))}
        </ol>
      )}
    </>
  );
}

/**
 * The reader's own instruction, and how today's brief answered it — FIRST,
 * because it is what they asked to hear first, and because an instruction the
 * brief could not follow must say so rather than leave a normal-looking morning.
 */
function Instruction({ instruction }: { instruction: NonNullable<AiBriefInsights['instruction']> }) {
  const { t } = useTranslation();
  if (instruction.note === '' && !hasNotes(instruction)) return null;
  return (
    <div className={instructionStyles.panel}>
      <p className={instructionStyles.panelLabel}>{t('aiAgent.instruction.label')}</p>
      {instruction.note !== '' && <p className={instructionStyles.note}>{instruction.note}</p>}
      <InstructionLines status={instruction} />
    </div>
  );
}

/**
 * The status at two lengths, the way an insight carries two.
 *
 * The short one is what a manager reads standing up; the long one is every
 * section with its numbers, for the reader who wanted more than three
 * sentences. It opens in place — a panel that pushed the list down would make
 * the short version cost something to expand.
 */
function Overview({ written }: { written: AiBriefInsights }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (written.overviewDetail === '') {
    return <p className={styles.overview}>{written.overview}</p>;
  }

  return (
    <>
      <p className={styles.overview}>
        {written.overview}{' '}
        <button
          type="button"
          className={styles.more}
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          <span className={styles.caret} data-collapsed={!open}>
            <ChevronDownIcon size={12} />
          </span>
          {t(open ? 'aiAgent.brief.less' : 'aiAgent.brief.more')}
        </button>
      </p>
      {open && <p className={styles.overviewDetail}>{written.overviewDetail}</p>}
    </>
  );
}

/**
 * An insight at two lengths: the sentence, and the specifics behind a click.
 *
 * Which trades, which floors, what two sections say together — a reader wants
 * all of it eventually and none of it while scanning. So the sentence is the
 * page, and the rest waits one press away at the end of it, opening in place
 * rather than in a panel that would move everything below.
 *
 * An insight with nothing to add renders as it always did: no control appears
 * for a longer version that does not exist.
 */
function Why({ insight }: { insight: AiBriefInsight }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (insight.detail === undefined) return <p className={styles.why}>{insight.why}</p>;

  return (
    <>
      <p className={styles.why}>
        {insight.why}{' '}
        <button
          type="button"
          className={styles.more}
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
        >
          <span className={styles.caret} data-collapsed={!open}>
            <ChevronDownIcon size={12} />
          </span>
          {t(open ? 'aiAgent.brief.less' : 'aiAgent.brief.more')}
        </button>
      </p>
      {open && <p className={styles.detail}>{insight.detail}</p>}
    </>
  );
}

interface IEvidenceProps {
  section: AiBriefSection['key'];
  state?: AiBriefSection['state'];
  hrefOf?: (section: AiBriefSection['key']) => string;
}

/**
 * Which reading an insight rests on: the mark that section wears, and — where
 * the surface holds the reading itself — a jump to it.
 */
function Evidence({ section, state, hrefOf }: IEvidenceProps) {
  const { t } = useTranslation();
  const title = t(`aiAgent.brief.sections.${section}.title`);
  const body = (
    <>
      <span className={styles.fromMark} aria-hidden />
      {title}
    </>
  );

  if (!hrefOf) {
    return (
      <span className={styles.from} data-state={state}>
        {body}
      </span>
    );
  }
  return (
    <a href={hrefOf(section)} className={styles.from} data-state={state}>
      {body}
    </a>
  );
}
